import { A } from '@adornis/base/env-info.js';
import { type Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ConvertConfig, FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { exportCSVFromEntities } from '@adornis/import-export/server/export-internals.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { createPDFFromComponent } from '@adornis/print/server/print-internals.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { stat } from 'fs/promises';
import { DateTime } from 'luxon';
import { TimeSlot } from '../datetime/time-slot.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { ExportError } from '../errors/ExportError.js';
import { getPluginForScope } from '../plugins/plugin.js';
import { isDefined } from '../util.js';
import { Participation } from './Participation.js';
import { ParticipationEinzelberatung } from './ParticipationEinzelberatung.js';
import { ParticipationEinzelberatungExportCSV } from './ParticipationExportCSV.js';
import { appointmentEndTimeValidation, appointmentStartTimeValidation } from './appointment.js';
import { BHFilter } from './bh-filter.js';
import { Product } from './products.js';
import { User } from './user.js';

/**
 * these config-values are used to generate available slots
 * and should therefore not be used for any other application logic
 * besides generating slots
 */
@Entity()
export class EinzelberatungSlotConfiguration extends AdornisEntity {
  static override _class = 'EinzelberatungSlotConfiguration';

  @validate(and(nonOptional(), appointmentStartTimeValidation))
  @Field(type => DateTime)
  start!: DateTime;

  @validate(and(nonOptional(), appointmentEndTimeValidation))
  @Field(type => DateTime)
  end!: DateTime;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  hasLunchBreak!: boolean;

  @validate(appointmentStartTimeValidation)
  @Field(type => DateTime)
  startLunch!: DateTime;

  @validate(appointmentEndTimeValidation)
  @Field(type => DateTime)
  endLunch!: DateTime;

  @Field(type => Int)
  slotDuration: Maybe<number>;

  @Field(type => [String], { default: v => v ?? [] })
  beraterIDs!: string[];
}

@Entity()
export class Einzelberatung extends Product {
  static override _class = 'Einzelberatung' as const;

  @Field(type => EinzelberatungSlotConfiguration)
  slotConfig: Maybe<EinzelberatungSlotConfiguration>;

  @Field(type => [EinzelberatungSlot], { default: v => v ?? [] })
  slots!: EinzelberatungSlot[];

  @DataLoadedField(type => [ParticipationEinzelberatung], einzelberatung => ({ productID: einzelberatung._id }))
  participations!: ParticipationEinzelberatung[];

  @Dependencies({
    classes: () => [Einzelberatung],
    ids: function (this: PartialEntityData<Einzelberatung>) {
      return [this._id!];
    },
  })
  @Field(type => DateTime, {
    resolve: function (this: Einzelberatung) {
      return async () => {
        if (this.startDate !== null && this.startDate !== undefined) return this.startDate;
        const einzelberatung = await getByID(Einzelberatung, this._id)({ slotConfig: { start: 1 } });
        return einzelberatung?.slotConfig?.start;
      };
    },
  })
  startDate: Maybe<DateTime>;

  @Query(type => String)
  static exportEinzelberatungParticipantsCSV(@Arg('einzelberatungID', type => ID) einzelberatungID: string) {
    return async () => {
      const counseling = await getByID(
        Einzelberatung,
        einzelberatungID,
      )({ institutionID: 1, slots: { _id: 1, start: 1, end: 1 } });
      if (!counseling) return;

      const participationCollection = await getRawCollection<ParticipationEinzelberatung>(
        ParticipationEinzelberatung._collectionName,
      );
      let pF = await publishFilter(ParticipationEinzelberatung._class);
      if (context.serverContext) pF = {};
      if (!pF) throw new Error(`Missing publishFilter for ${ParticipationEinzelberatung._class}`);
      const exportData = participationCollection.aggregate<ParticipationEinzelberatungExportCSV>([
        {
          $match: {
            $and: [
              {
                productID: einzelberatungID,
              },
              pF,
            ],
          },
        },
        {
          $replaceRoot: {
            newRoot: {
              participation: {
                $arrayToObject: {
                  $map: {
                    input: { $objectToArray: '$$ROOT' },
                    as: 'field',
                    in: {
                      k: '$$field.k',
                      v: '$$field.v',
                    },
                  },
                },
              },
            },
          },
        },
        {
          $lookup: {
            from: User._collectionName,
            localField: 'participation.userID',
            foreignField: '_id',
            as: 'user',
          },
        },
        {
          $addFields: {
            user: { $arrayElemAt: ['$user', 0] },
          },
        },
        {
          $lookup: {
            from: Product._collectionName,
            localField: 'participation.productID',
            foreignField: '_id',
            as: 'product',
          },
        },
        {
          $addFields: {
            product: { $arrayElemAt: ['$product', 0] },
          },
        },
        {
          $addFields: {
            slot: {
              $filter: {
                input: '$product.slots',
                as: 'slot',
                cond: {
                  $eq: ['$$slot._id', '$participation.timeslotID'],
                },
              },
            },
          },
        },
        {
          $addFields: {
            slot: { $arrayElemAt: ['$slot', 0] },
          },
        },
        {
          $lookup: {
            from: User._collectionName,
            localField: 'slot.beraterID',
            foreignField: '_id',
            as: 'berater',
          },
        },
        {
          $addFields: { berater: { $arrayElemAt: ['$berater', 0] } },
        },
        {
          $lookup: {
            from: 'user-stammdaten',
            localField: 'user._id',
            foreignField: 'userID',
            as: 'stammdaten',
          },
        },
        {
          $unwind: '$stammdaten',
        },
        {
          $addFields: {
            _class: ParticipationEinzelberatungExportCSV._class,
          },
        },
      ]);

      const node = getPluginForScope('registration::stammdaten', counseling.institutionID).classType();
      const dynamicFields = Object.values(node.fields);
      // TODO(diogo): Should i include the  static fields hier?

      const filePath = await exportCSVFromEntities(
        new ConvertConfig<ParticipationEinzelberatungExportCSV>({
          className: ParticipationEinzelberatungExportCSV._class,
          delimiter: ';',
          fieldMappings: [
            { input: 'ID', output: 'participation._id' },
            { input: 'Vorname', output: 'user.firstName' },
            { input: 'Nachname', output: 'user.lastName' },
            { input: 'E-Mail', output: 'user.email' },
            { input: 'Status', output: 'participation.state' },
            new FieldMappingEntry(
              { input: 'Wartelistenplatz' },
              undefined,
              (v: any, data: ParticipationEinzelberatungExportCSV, i: number) =>
                data.participation.state === ParticipationState.WAITING
                  ? data.participation.waitingListPlace?.toString() ?? '-'
                  : '-',
            ),
            new FieldMappingEntry(
              { input: 'Letzte Änderung' },
              undefined,
              (v: any, data: ParticipationEinzelberatungExportCSV, i: number) =>
                data.participation.updatedAt.toFormat('dd.LL.yyyy'),
            ),
            // ...dynamicFields.map(field => {
            //   return new FieldMappingEntry(
            //     { input: field.name },
            //     undefined,
            //     (v: any, data: ParticipationEinzelberatungExportCSV, i: number) => {
            //       const value = data.stammdaten[field.name];
            //       if (value instanceof DateTime) return value.toLocaleString(DateTime.DATE_MED);
            //       return data.stammdaten[field.name];
            //     },
            //   );
            // }),
            new FieldMappingEntry(
              { input: 'Berater' },
              undefined,
              (v: any, data: ParticipationEinzelberatungExportCSV, i: number) =>
                `${data.berater?.firstName} ${data.berater?.lastName}`,
            ),
            new FieldMappingEntry(
              { input: 'Zeitslot' },
              undefined,
              (v: any, data: ParticipationEinzelberatungExportCSV, i: number) => {
                const zeitslot = counseling.slots.find(slot => slot._id === data.participation.timeslotID);
                if (!zeitslot) return '---';
                return `${zeitslot.start.toLocaleString(DateTime.DATETIME_MED)} - ${zeitslot.end.toLocaleString(
                  DateTime.DATETIME_MED,
                )}`;
              },
            ),
          ],
        }),
        exportData,
        'export-teilnehmer',
      );

      try {
        const stats = await stat(filePath);
        if (stats.size === 0) throw new Error('file is empty');
      } catch (e) {
        throw new Error('file is empty');
      }

      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  }

  @Subscription(type => [Einzelberatung])
  static subscribeCounselingsPaginated(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => BHFilter) filter: BHFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<Einzelberatung>) => {
      if (!filter.institutionID) return [];

      return this.subscribePaginatedAggregation(skip, limit, filter.getMongoFilter(), [
        {
          $match: {
            _class: Einzelberatung._class,
            institutionID: filter.institutionID,
          },
        },
        { $sort: filter.getMongoOptions().sort },
      ]);
    };
  }

  @Query(type => String)
  static removeEinzelberatungByID(@Arg('counselingID', type => String) counselingID: string) {
    return async () => {
      if (context.serverContext) return;
      const contextUserID = context.userID as string;
      if (!contextUserID) throw new Error('user not logged in');
      const currentUser = await getByID(User, contextUserID)({ roles: AdornisRoleToContextsWrapper.allFields });
      if (!currentUser) throw new Error('user not logged in');
      const counseling = await Einzelberatung.getByID<Einzelberatung>(counselingID)(Einzelberatung.allFields);
      if (!counseling) throw new Error('workshop not found');
      const hasPermission = currentUser.hasPermission(
        Permission.EDIT_ADMIN_INSTITUTION_COUNSELINGS,
        counseling.institutionID,
      );
      if (!hasPermission) throw new Error('no permission to do that');
      const collectionParticipation = await getRawCollection<Participation>(Participation._collectionName);

      // remove participations
      await collectionParticipation.deleteMany({ productID: counselingID });
      // remove workshop
      await counseling.remove();
    };
  }

  @Query(type => [Einzelberatung])
  static getEinzelberatungByBeraterID(@Arg('beraterID', type => String) beraterID: string) {
    return async (gqlFields: BaseQLSelectionSet<Einzelberatung>) => {
      const collection = await getCollection<Einzelberatung>(this._class);
      const result = await collection
        .find<Einzelberatung>({
          beraterAvailabilities: {
            $elemMatch: {
              beraterID,
            },
          },
        })
        .toArray();
      return result;
    };
  }

  @Query(type => Int)
  static getEinzelberatungSlotCount(@Arg('filter', type => BHFilter) filter?: BHFilter) {
    return async () => {
      const collection = await getRawCollection<Einzelberatung>(this._collectionName);
      const result = await collection
        .aggregate([
          {
            $match: {
              _class: this._class,
              ...(filter?.productID ? { _id: filter?.productID } : {}),
              ...(filter?.institutionID ? { institutionID: filter.institutionID } : {}),
              ...(filter?.timeframeID ? { timeframeID: filter?.timeframeID } : {}),
              ...(filter?.categoryID ? { categoryID: filter?.categoryID } : {}),
            },
          },
          {
            $project: {
              slotCount: { $size: '$slots' },
            },
          },
          {
            $group: {
              _id: null,
              totalSum: { $sum: '$slotCount' },
            },
          },
        ])
        .toArray();

      return result[0]?.totalSum ?? 0;
    };
  }

  @Query(type => String)
  static getBeraterSlotPdfLink(@Arg('einzelberatungID', type => String) einzelberatungID: string) {
    return async () => {
      const queryParams = new URLSearchParams({ counselingID: einzelberatungID }).toString();
      const invoice = await createPDFFromComponent({
        component: 'counseling-adivsor-slots',
        timeout: 10 * 1000,
        queryParams,
        fileName: `einzelberatung_${einzelberatungID}_teilnehmerubersicht.pdf`,
      });

      return A.absoluteUrl(invoice);
    };
  }
}

@Entity()
export class EinzelberatungSlot extends TimeSlot {
  static override _class = 'BeraterAvailabilities';

  // this should be a userID
  @validate(nonOptional())
  @Field(type => String)
  beraterID: Maybe<string>;

  @Field(type => User, {
    resolve(this: EinzelberatungSlot) {
      return async () => getByID(User, this.beraterID as string)(User.allFields);
    },
  })
  berater: Maybe<User>;

  @Dependencies({
    classes: () => [ParticipationEinzelberatung],
    ids: async function (this: PartialEntityData<EinzelberatungSlot>) {
      const c = await getRawCollection(ParticipationEinzelberatung._collectionName);
      const participations = (await c.find({ timeslotID: this._id }).toArray()).filter(
        p => p.state === ParticipationState.IN,
      );
      return participations.map(p => p._id as any as string).filter(isDefined);
    },
  })
  @Field(type => Boolean, {
    resolve(this: EinzelberatungSlot) {
      return async () => {
        const c = await getRawCollection(ParticipationEinzelberatung._collectionName);
        const participations = (await c.find({ timeslotID: this._id }).toArray()).filter(
          p => p.state === ParticipationState.IN,
        );
        return participations.length > 0; // isBooked when participation exists
      };
    },
  })
  isBooked!: boolean;
}
